import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Monitor, Tablet, Smartphone, RefreshCw, ExternalLink } from 'lucide-react';

export function getStorefrontUrl() {
  if (typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')) {
    return process.env.REACT_APP_STOREFRONT_URL || 'http://localhost:3001';
  }
  return process.env.REACT_APP_STOREFRONT_URL || 'https://cq.letinaisolution.com';
}

const MESSAGE_SECTIONS = 'cq-cms-preview';
const MESSAGE_PRODUCTS = 'cq-cms-products';
const MESSAGE_PANEL = 'cq-cms-panel';
const MESSAGE_SCROLL = 'cq-cms-scroll';
const MESSAGE_READY = 'cq-preview-ready';

/**
 * The frame is always rendered at a real device width and then scaled down to
 * fit the rail — otherwise a 460px pane would make the site render its mobile
 * layout and the editor would be previewing the wrong thing.
 */
const DEVICES = [
  { id: 'desktop', label: 'Desktop', width: 1440, height: 900, icon: Monitor },
  { id: 'tablet', label: 'Tablet', width: 834, height: 1112, icon: Tablet },
  { id: 'mobile', label: 'Mobile', width: 390, height: 844, icon: Smartphone }
];

export default function CmsPreviewPane({ pagePath, sections, activeSectionKey, reloadToken, productOrder, forcePanel }) {
  const iframeRef = useRef(null);
  const stageRef = useRef(null);
  const readyRef = useRef(false);
  const productOrderRef = useRef(null);
  const forcePanelRef = useRef(null);
  const pendingRef = useRef(null);
  const timerRef = useRef(null);

  const [deviceId, setDeviceId] = useState('desktop');
  const [connected, setConnected] = useState(false);
  const [nonce, setNonce] = useState(0);
  const [stage, setStage] = useState({ width: 0, height: 0 });

  const STOREFRONT = getStorefrontUrl();
  const device = DEVICES.find((d) => d.id === deviceId) || DEVICES[0];
  const src = `${STOREFRONT}${pagePath}?preview=1&v=${reloadToken || 0}-${nonce}`;

  // Fit the device viewport into whatever width the rail has
  useLayoutEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => setStage({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const scale = stage.width ? Math.min(1, stage.width / device.width) : 0;
  const frameHeight = scale ? Math.max(device.height, stage.height / scale) : device.height;

  /** Shape the admin's working copy back into what the public API returns */
  const buildPayload = useCallback(() => {
    const out = {};
    Object.entries(sections || {}).forEach(([key, section]) => {
      if (!section) return;
      const copy = {
        ...section,
        extraConfigJson:
          section.extraConfigParsed !== undefined
            ? JSON.stringify(section.extraConfigParsed)
            : section.extraConfigJson
      };
      delete copy.extraConfigParsed;
      out[key] = copy;
    });
    return out;
  }, [sections]);

  const post = useCallback((message) => {
    const frame = iframeRef.current;
    if (!frame || !frame.contentWindow) return;
    frame.contentWindow.postMessage(message, '*');
  }, []);

  // Handshake — the page tells us when its listener is attached
  useEffect(() => {
    const onMessage = (event) => {
      const allowedOrigins = [
        STOREFRONT,
        'https://cq.letinaisolution.com',
        'https://thecaratquest.com',
        'http://localhost:3000',
        'http://localhost:3001',
      ];
      if (!allowedOrigins.includes(event.origin)) return;
      if (event.data?.type !== MESSAGE_READY) return;
      readyRef.current = true;
      setConnected(true);
      if (pendingRef.current) {
        post({ type: MESSAGE_SECTIONS, sections: pendingRef.current });
        pendingRef.current = null;
      }
      if (productOrderRef.current) post({ type: MESSAGE_PRODUCTS, order: productOrderRef.current });
      post({ type: MESSAGE_PANEL, panel: forcePanelRef.current });
      if (activeSectionKey) post({ type: MESSAGE_SCROLL, sectionKey: activeSectionKey });
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [post, activeSectionKey, STOREFRONT]);

  // A hover panel cannot be inspected by hovering inside the frame, so the
  // editor asks the page to hold the one it is working on open.
  useEffect(() => {
    forcePanelRef.current = forcePanel || null;
    if (!readyRef.current) return;
    post({ type: MESSAGE_PANEL, panel: forcePanel || null });
  }, [forcePanel, post]);

  // Pieces moved with the arrows are not saved yet, so the order travels the
  // same way the section edits do rather than waiting for a reload.
  const orderKey = (productOrder || []).join('|');
  useEffect(() => {
    productOrderRef.current = productOrder || null;
    if (!readyRef.current || !productOrder) return;
    post({ type: MESSAGE_PRODUCTS, order: productOrder });
  }, [orderKey, productOrder, post]);

  // Push edits, debounced so a fast typist does not flood the frame
  useEffect(() => {
    const payload = buildPayload();
    if (!readyRef.current) {
      pendingRef.current = payload;
      return;
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => post({ type: MESSAGE_SECTIONS, sections: payload }), 150);
    return () => clearTimeout(timerRef.current);
  }, [buildPayload, post]);

  // Jump to whichever section the editor is working on
  useEffect(() => {
    if (!readyRef.current || !activeSectionKey) return;
    post({ type: MESSAGE_SCROLL, sectionKey: activeSectionKey });
  }, [activeSectionKey, post]);

  // A new page or a save means a fresh document
  useEffect(() => {
    readyRef.current = false;
    setConnected(false);
  }, [pagePath, reloadToken]);

  const reload = () => {
    readyRef.current = false;
    setConnected(false);
    setNonce((n) => n + 1);
  };

  const iconBtn = (active) => ({
    background: active ? '#2563eb' : 'transparent',
    color: active ? '#fff' : '#64748b',
    border: 'none',
    borderRadius: 6,
    padding: '5px 6px',
    cursor: 'pointer',
    display: 'flex'
  });

  return (
    <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, background: '#fff', overflow: 'hidden', display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Toolbar */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '7px 10px', borderBottom: '1px solid #e2e8f0', background: '#f8fafc' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <span style={{ width: 7, height: 7, borderRadius: '50%', background: connected ? '#16a34a' : '#cbd5e1', flexShrink: 0 }} />
          <span style={{ fontSize: 10.5, fontWeight: 800, color: '#334155', textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>
            {connected ? 'Live' : 'Connecting'}
          </span>
          {scale > 0 && (
            <span style={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>
              {device.width}px · {Math.round(scale * 100)}%
            </span>
          )}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {DEVICES.map((d) => {
            const Icon = d.icon;
            return (
              <button key={d.id} type="button" title={d.label} onClick={() => setDeviceId(d.id)} style={iconBtn(deviceId === d.id)}>
                <Icon size={13} />
              </button>
            );
          })}
          <span style={{ width: 1, height: 14, background: '#cbd5e1', margin: '0 3px' }} />
          <button type="button" title="Reload preview" onClick={reload} style={iconBtn(false)}>
            <RefreshCw size={13} />
          </button>
          <a href={`${STOREFRONT}${pagePath}`} target="_blank" rel="noreferrer" title="Open in a new tab" style={{ ...iconBtn(false), textDecoration: 'none' }}>
            <ExternalLink size={13} />
          </a>
        </div>
      </div>

      {/* Scaled stage */}
      <div
        ref={stageRef}
        style={{ flex: 1, background: '#0f172a', overflow: 'hidden', position: 'relative' }}
      >
        {scale > 0 && (
          <iframe
            ref={iframeRef}
            key={src}
            src={src}
            title="Website preview"
            style={{
              width: device.width,
              height: frameHeight,
              border: 'none',
              background: '#fff',
              transform: `scale(${scale})`,
              transformOrigin: 'top left',
              position: 'absolute',
              top: 0,
              left: stage.width > device.width * scale ? (stage.width - device.width * scale) / 2 : 0
            }}
          />
        )}
      </div>

      <div style={{ padding: '5px 10px', borderTop: '1px solid #e2e8f0', background: '#f8fafc', fontSize: 10, color: '#94a3b8', textAlign: 'center' }}>
        Live — not saved until you press Publish Section Live
      </div>
    </div>
  );
}
